import { Box, Stack, Theme, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { PositionRoleEnum } from "@sinch/core/entities/serverEnums.types";
import { isNotNil } from "ramda-adjunct";
import React, { FC, ReactNode } from "react";
import { DateRange, MdsIcon } from "@sinch/components";
import { usePositionRole } from "@sinch/components/application/PositionRole/PositionRole";
import { EntityPill, PositionPill } from "@sinch/components/Pill/Pill";
import { useTranslation } from "react-i18next";
import { VIRTUAL_POSITION_PREFIX } from "./virtualPosition";

export type PositionNameProps = {
  id?: number | string | null;
  name?: ReactNode | null;
  staffName?: ReactNode | null;
  role?: PositionRoleEnum | null;
  variant?: "smallTitle" | "regularLine";
} & (
  | {
      capacity: number;
      staffCount: number;
    }
  | {
      capacity?: undefined;
      staffCount?: undefined;
    }
) &
  (
    | {
        beginning: Date;
        end: Date;
      }
    | {
        beginning?: undefined;
        end?: undefined;
      }
  );

export const PositionName: FC<PositionNameProps> = ({
  variant,
  name,
  staffName,
  capacity,
  role,
  staffCount,
  id,
  beginning,
  end,
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const { getRoleIcon, getRoleColor, getRoleLabel } = usePositionRole();

  return (
    <Stack alignItems="center" direction="row" flexWrap={isMobile ? "wrap" : "nowrap"} spacing={1}>
      {role && (
        <Tooltip title={getRoleLabel(role)}>
          <MdsIcon
            fontSize={variant === "regularLine" ? "small" : "medium"}
            icon={getRoleIcon(role)}
            sx={({ spacing }) => ({ color: getRoleColor(role), marginRight: spacing(1) })}
          />
        </Tooltip>
      )}
      {name && (
        <Typography
          component="span"
          sx={(theme) => ({
            fontSize: variant === "regularLine" ? theme.typography.body1.fontSize : theme.typography.pxToRem(15),
          })}
        >
          {name}
        </Typography>
      )}
      {staffName && (
        <Typography
          component="span"
          sx={(theme) => ({
            fontSize: variant === "regularLine" ? theme.typography.body1.fontSize : theme.typography.pxToRem(15),
          })}
        >
          {" – "}
          {staffName}
        </Typography>
      )}
      {isNotNil(staffCount) && isNotNil(capacity) && (
        <Typography
          component="span"
          sx={(theme) => ({
            fontSize: variant === "regularLine" ? theme.typography.body1.fontSize : theme.typography.pxToRem(15),
            color: staffCount < capacity ? theme.palette.error.main : theme.palette.success.main,
          })}
        >
          ({staffCount}/{capacity})
        </Typography>
      )}
      {id && !String(id).startsWith(`${VIRTUAL_POSITION_PREFIX}:`) && (
        <Box>
          <PositionPill id={id} size="small" />
        </Box>
      )}
      {id && String(id).startsWith(`${VIRTUAL_POSITION_PREFIX}:`) && (
        <Box>
          <EntityPill labelColor={({ palette }) => palette.warning.main} size="small">
            {t("new")}
          </EntityPill>
        </Box>
      )}
      {beginning && end && (
        <Typography
          component="span"
          sx={({ typography }) => ({
            whiteSpace: "nowrap",
            fontSize: variant === "regularLine" ? typography.descriptionTiny.fontSize : typography.body1.fontSize,
          })}
        >
          <DateRange end={end} start={beginning} />
        </Typography>
      )}
    </Stack>
  );
};
